
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { logger } from '@/lib/logger';
import { toastService } from '@/services/ToastService';

export interface UserProfile {
  id: string;
  nome: string;
  telefone: string;
  profissao: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (
    email: string,
    password: string,
    userData: { nome: string; telefone: string; profissao: string }
  ) => Promise<{ error: any; emailDeliveryError?: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (userData: { nome: string; telefone: string; profissao: string }) => Promise<{ error: any }>;
  refreshSession: () => Promise<boolean>;
  checkSessionHealth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

// Configurações otimizadas - reduzidas para menos spam
const SESSION_REFRESH_THRESHOLD = 600; // 10 minutos em segundos
const SESSION_CHECK_INTERVAL_DESKTOP = 300000; // 5 minutos em ms (aumentado)
const SESSION_CHECK_INTERVAL_MOBILE = 120000; // 2 minutos em ms (aumentado)
const RETRY_ATTEMPTS_LIMIT = 3; // Reduzido para menos tentativas
const MOBILE_REFRESH_TIMEOUT = 20000; // 20 segundos para mobile
const DESKTOP_REFRESH_TIMEOUT = 10000; // 10 segundos para desktop
const NETWORK_RETRY_DELAY = 3000; // 3 segundos de delay entre tentativas

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshInProgress, setRefreshInProgress] = useState<boolean>(false);
  const [retryAttempts, setRetryAttempts] = useState<number>(0);
  const authInitialized = useRef<boolean>(false);
  const refreshTimeoutRef = useRef<number | null>(null);
  const sessionCheckIntervalRef = useRef<number | null>(null);
  const isMobile = useIsMobile();
  const sessionCheckInterval = isMobile ? SESSION_CHECK_INTERVAL_MOBILE : SESSION_CHECK_INTERVAL_DESKTOP;
  const visibilityChangeRef = useRef<(() => void) | null>(null);
  const focusEventRef = useRef<(() => void) | null>(null);
  const connectionToastShown = useRef<boolean>(false);

  // Enhanced subscription sync function
  const syncSubscription = async () => {
    try {
      console.log('Auth: syncing subscription with Stripe');
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.warn('Auth: check-subscription failed', error);
      } else {
        console.log('Auth: subscription sync successful', data);
      }
    } catch (e) {
      console.warn('Auth: check-subscription exception', e);
    }
  };

  // Fetch profile data
  const fetchProfile = async (userId: string) => {
    try {
      logger.debug('Auth', `Fetching profile for user: ${userId.substring(0, 8)}...`);
      const { data, error } = await supabase
        .from('perfis')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        logger.error('Auth', 'Failed to fetch user profile', error);
        return null;
      }

      return data as UserProfile;
    } catch (error) {
      logger.error('Auth', 'Exception while fetching profile', error);
      return null;
    }
  };

  // Check if the session is healthy and needs refresh
  const checkSessionHealth = async (): Promise<boolean> => {
    if (!session) {
      return false;
    }

    try {
      // Check if token is getting close to expiry
      const expiresAt = session.expires_at ? session.expires_at : 0;
      const nowInSeconds = Math.floor(Date.now() / 1000);
      const secondsUntilExpiry = expiresAt - nowInSeconds;
      
      // If token is expiring soon, refresh it proactively
      if (secondsUntilExpiry < SESSION_REFRESH_THRESHOLD) {
        logger.info('Auth', `Session expiring in ${secondsUntilExpiry}s, refreshing proactively`);
        return await refreshSession();
      }
      
      return true; // Session is healthy
    } catch (error) {
      logger.error('Auth', 'Session health check failed', error);
      return false;
    }
  };

  // Detectar conectividade de rede
  const isOnline = () => {
    return navigator.onLine;
  };

  // Esperar pela rede ficar disponível
  const waitForNetwork = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (isOnline()) {
        resolve(true);
        return;
      }

      const handleOnline = () => {
        window.removeEventListener('online', handleOnline);
        resolve(true);
      };

      window.addEventListener('online', handleOnline);
      
      // Timeout após 30 segundos
      setTimeout(() => {
        window.removeEventListener('online', handleOnline);
        resolve(false);
      }, 30000);
    });
  };

  // Attempt to refresh the session token with improved mobile handling
  const refreshSession = async (): Promise<boolean> => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (refreshInProgress) {
      return false;
    }

    try {
      setRefreshInProgress(true);
      logger.debug('Auth', `Refreshing session (mobile: ${isMobile})`);

      // Verificar conectividade antes de tentar
      if (!isOnline()) {
        logger.warn('Auth', 'Offline detected, waiting for network');
        if (!connectionToastShown.current && isMobile) {
          toastService.connectionRetrying();
          connectionToastShown.current = true;
        }
        const networkAvailable = await waitForNetwork();
        if (!networkAvailable) {
          logger.error('Auth', 'Network timeout during session refresh');
          return false;
        }
      }

      // Timeout adaptado para mobile
      const refreshTimeout = isMobile ? MOBILE_REFRESH_TIMEOUT : DESKTOP_REFRESH_TIMEOUT;
      
      const refreshPromise = supabase.auth.refreshSession();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Refresh timeout')), refreshTimeout);
      });

      const { data, error } = await Promise.race([refreshPromise, timeoutPromise]);

      if (error) {
        logger.error('Auth', 'Session refresh failed', error);
        
        // Casos especiais de erro
        if (error.message.includes('refresh_token_not_found') || 
            error.message.includes('Invalid Refresh Token')) {
          logger.warn('Auth', 'Invalid refresh token, forcing logout');
          setRetryAttempts(0); // Reset counter
          await handleSignOut(true);
          return false;
        }
        
        // Retry logic with exponential backoff
        if (retryAttempts < RETRY_ATTEMPTS_LIMIT) {
          setRetryAttempts(prev => prev + 1);
          const baseDelay = isMobile ? NETWORK_RETRY_DELAY : 1000;
          const backoffTime = Math.min(baseDelay * Math.pow(2, retryAttempts), 10000);
          
          logger.info('Auth', `Retry attempt ${retryAttempts + 1}/${RETRY_ATTEMPTS_LIMIT} in ${backoffTime}ms`);
          
          setTimeout(() => {
            refreshSession();
          }, backoffTime);
          return false;
        }
        
        // Failed on all attempts - show error only once
        if (error.message.includes('timeout')) {
          toastService.error('Tempo limite de conexão excedido', 'Verifique sua internet');
        } else if (retryAttempts >= RETRY_ATTEMPTS_LIMIT) {
          toastService.error('Problemas de conectividade', 'Tente fazer login novamente');
        }
        
        // If we're on mobile and there were many errors, force logout
        if (isMobile && retryAttempts >= RETRY_ATTEMPTS_LIMIT) {
          await handleSignOut(true);
        }
        
        return false;
      }
      
      // Reset retry counter and connection toast flag on success
      setRetryAttempts(0);
      connectionToastShown.current = false;

      if (!data.session) {
        logger.error('Auth', 'No session returned after refresh');
        return false;
      }

      logger.info('Auth', 'Session refreshed successfully');
      
      // In mobile, show positive feedback only if there were previous connection issues
      if (isMobile && retryAttempts > 0) {
        toastService.connectionRestored();
      }
      
      setSession(data.session);
      setUser(data.session.user);

      // Fetch profile asynchronously to not block
      setTimeout(() => {
        fetchProfile(data.session.user.id).then(profileData => {
          setProfile(profileData);
        });
      }, 0);

      return true;
    } catch (error: any) {
      logger.error('Auth', 'Unexpected error during session refresh', error);
      
      if (error.message.includes('timeout')) {
        toastService.error('Tempo limite de conexão excedido', 'Verifique sua internet');
      }
      
      return false;
    } finally {
      setRefreshInProgress(false);
    }
  };

  // Handle sign out with improved error handling
  const handleSignOut = async (expired = false) => {
    try {
      logger.info('Auth', `Signing out user (expired: ${expired})`);
      localStorage.removeItem('dashboardDataTimestamp');

      // Clear session check interval
      if (sessionCheckIntervalRef.current) {
        window.clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }

      // Clean up event listeners
      removeVisibilityAndFocusEventListeners();

      // Clear auth state before API call
      setUser(null);
      setProfile(null);
      setSession(null);
      
      // Set loading to false to prevent infinite loading states
      setLoading(false);

      const { error } = await supabase.auth.signOut({ scope: 'global' });

      if (error) {
        logger.error('Auth', 'Error during signOut', error);
        toastService.authLogoutError();
      } else {
        // Only show success toast if not expired
        if (!expired) {
          toastService.authLogoutSuccess();
        }
      }

      if (expired) {
        toastService.authSessionExpired();
      }
    } catch (error) {
      logger.error('Auth', 'Exception during signOut', error);
      toastService.authLogoutError();
      
      // Ensure user is still logged out locally even if API call fails
      setUser(null);
      setProfile(null);
      setSession(null);
      setLoading(false);
    }
  };

  // Configurar event listeners para visibilidade, foco e conectividade
  const setupVisibilityAndFocusEventListeners = () => {
    // Handler otimizado para quando o app volta a ficar visível (importante no mobile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logger.debug('Auth', 'App became visible, checking session health');
        // Em mobile, dar um pequeno delay para estabilizar
        setTimeout(() => {
          if (session && user) {
            checkSessionHealth();
          }
        }, isMobile ? 500 : 100);
      }
    };

    // Handler para quando a janela recupera o foco
    const handleFocus = () => {
      logger.debug('Auth', 'App gained focus, checking session health');
      if (session && user) {
        checkSessionHealth();
      }
    };

    // Handler para mudanças de conectividade (importante no mobile)
    const handleOnline = () => {
      logger.info('Auth', 'Network connectivity restored');
      connectionToastShown.current = false; // Reset flag
      if (session && user) {
        setTimeout(() => {
          checkSessionHealth();
        }, 1000); // Delay para estabilizar a conexão
      }
    };

    const handleOffline = () => {
      logger.warn('Auth', 'Network connectivity lost');
      // Em mobile, mostrar feedback ao usuário apenas uma vez
      if (isMobile && !connectionToastShown.current) {
        toastService.connectionLost();
        connectionToastShown.current = true;
      }
    };

    // Armazenar referências para remoção posterior
    visibilityChangeRef.current = handleVisibilityChange;
    focusEventRef.current = handleFocus;

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  };

  // Remover event listeners
  const removeVisibilityAndFocusEventListeners = () => {
    if (visibilityChangeRef.current) {
      document.removeEventListener('visibilitychange', visibilityChangeRef.current);
      visibilityChangeRef.current = null;
    }

    if (focusEventRef.current) {
      window.removeEventListener('focus', focusEventRef.current);
      window.removeEventListener('online', focusEventRef.current);
      window.removeEventListener('offline', focusEventRef.current);
      focusEventRef.current = null;
    }
  };

  // Setup periodic session health checks
  useEffect(() => {
    if (session && !sessionCheckIntervalRef.current) {
      logger.debug('Auth', `Setting up session health checks every ${sessionCheckInterval/1000}s`);
      sessionCheckIntervalRef.current = window.setInterval(() => {
        checkSessionHealth().then(healthy => {
          if (!healthy) {
            logger.warn('Auth', 'Session health check failed');
          }
        });
      }, sessionCheckInterval);

      // Configurar listeners para eventos de visibilidade e foco
      setupVisibilityAndFocusEventListeners();
    }

    return () => {
      if (sessionCheckIntervalRef.current) {
        window.clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
      removeVisibilityAndFocusEventListeners();
    };
  }, [session, isMobile, sessionCheckInterval]);

  // Update authentication state when session changes
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        setLoading(true);
        logger.info('Auth', 'Initializing authentication');

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, newSession) => {
            logger.debug('Auth', `Auth state changed: ${event}`);
            if (!mounted) return;

            if (event === 'SIGNED_OUT' || !newSession) {
              setUser(null);
              setProfile(null);
              setSession(null);
              setLoading(false);
              if (sessionCheckIntervalRef.current) {
                window.clearInterval(sessionCheckIntervalRef.current);
                sessionCheckIntervalRef.current = null;
              }
              removeVisibilityAndFocusEventListeners();
            } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
              logger.info('Auth', `User ${event.toLowerCase()}`);
              setSession(newSession);
              setUser(newSession.user);

              // Enhanced subscription sync on auth events
              setTimeout(() => {
                if (mounted) {
                  syncSubscription();
                }
              }, 1000); // Add delay to ensure user is fully authenticated

              setTimeout(() => {
                if (mounted) {
                  fetchProfile(newSession.user.id).then(profileData => {
                    if (mounted) {
                      setProfile(profileData);
                      setLoading(false);
                    }
                  });
                }
              }, 0);
            } else {
              setLoading(false);
            }
          }
        );

        const { data: { session: currentSession }, error } = await supabase.auth.getSession();

        if (error) {
          logger.error('Auth', 'Failed to get initial session', error);
          throw error;
        }

        if (!mounted) return;

        if (currentSession) {
          logger.info('Auth', 'Existing session found');
          setSession(currentSession);
          setUser(currentSession.user);
          const profileData = await fetchProfile(currentSession.user.id);
          setProfile(profileData);

          // Enhanced subscription sync on initialization
          setTimeout(() => {
            if (mounted) {
              syncSubscription();
              checkSessionHealth();
            }
          }, 1000);
        } else {
          setUser(null);
          setProfile(null);
          setSession(null);
        }

        authInitialized.current = true;
      } catch (error) {
        logger.error('Auth', 'Failed to initialize authentication', error);
        setUser(null);
        setProfile(null);
        setSession(null);
      } finally {
        if (mounted) {
          logger.info('Auth', 'Authentication initialization completed');
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
      if (sessionCheckIntervalRef.current) {
        window.clearInterval(sessionCheckIntervalRef.current);
      }
      removeVisibilityAndFocusEventListeners();
    };
  }, []);

  // Sign in with email and password - REMOVED TOAST (handled in UI)
  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        logger.error('Auth', 'Sign in failed', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      logger.error('Auth', 'Exception during sign in', error);
      return { error };
    }
  };

  // Sign up with email and password
  const signUp = async (
    email: string,
    password: string,
    userData: { nome: string; telefone: string; profissao: string }
  ) => {
    try {
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nome: userData.nome,
            telefone: userData.telefone,
            profissao: userData.profissao,
          },
        },
      });

      if (error) {
        if (error.message.includes('already')) {
          logger.error('Auth', 'Email already in use during signup');
          toastService.error('Este email já está em uso');
        } else {
          logger.error('Auth', 'Signup failed', error);
          toastService.error('Erro ao criar conta');
        }
        return { error };
      }

      if (data?.user && !data?.user?.confirmed_at) {
        if (!data?.user?.confirmation_sent_at) {
          logger.error('Auth', 'Email confirmation not sent');
          return { error: null, emailDeliveryError: true };
        }
      }

      toastService.success('Conta criada! Verifique seu email');
      return { error: null };
    } catch (error) {
      logger.error('Auth', 'Exception during signup', error);
      toastService.error('Ocorreu um erro, tente novamente');
      return { error };
    }
  };

  // Sign out
  const signOut = async () => {
    await handleSignOut();
  };

  // Reset password - Função melhorada
  const resetPassword = async (email: string) => {
    try {
      // Construir a URL de redirecionamento completa com protocolo
      const redirectUrl = `${window.location.origin}/reset-password-confirm`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        logger.error('Auth', 'Password reset failed', error);
        toastService.error('Erro ao enviar email de recuperação');
        return { error };
      }

      toastService.success('Email de recuperação enviado');
      return { error: null };
    } catch (error) {
      logger.error('Auth', 'Exception during password reset', error);
      toastService.error('Ocorreu um erro, tente novamente');
      return { error };
    }
  };

  // Update user profile
  const updateProfile = async (userData: { nome: string; telefone: string; profissao: string }) => {
    try {
      if (!user) {
        return { error: new Error('Usuário não autenticado') };
      }

      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          nome: userData.nome,
          telefone: userData.telefone,
          profissao: userData.profissao,
        },
      });

      if (metadataError) {
        logger.error('Auth', 'Failed to update auth metadata', metadataError);
        toastService.error('Erro ao atualizar dados de autenticação');
        return { error: metadataError };
      }

      const { error: profileError } = await supabase
        .from('perfis')
        .update({
          nome: userData.nome,
          telefone: userData.telefone,
          profissao: userData.profissao,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (profileError) {
        logger.error('Auth', 'Failed to update profile', profileError);
        toastService.error('Erro ao atualizar perfil');
        return { error: profileError };
      }

      setProfile({
        ...profile as UserProfile,
        nome: userData.nome,
        telefone: userData.telefone,
        profissao: userData.profissao,
      });

      toastService.success('Perfil atualizado com sucesso');
      return { error: null };
    } catch (error) {
      logger.error('Auth', 'Exception updating profile', error);
      toastService.error('Ocorreu um erro, tente novamente');
      return { error };
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    refreshSession,
    checkSessionHealth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
